import { Label } from "@monsterindia/global-ui-components";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";

import { SalaryModeOptions } from "../../../constants/employment";
import { getSalaryBasedOnSiteContext } from "../../../helpers/getSalaryBasedOnSiteContext";
import { getSiteProps } from "../../../utils/siteProps";
import { SalaryInputController } from "../../controllers";
import { ActionButtonContainer } from "../common/ActionButtonContainer";
import { Heading } from "../common/Heading";
import { HighLowSalaryWarning } from "./HighLowSalaryWarning";
import { SalaryUIProps } from "./Salary.types";

export const SalaryUI = ({
  prefilledData,
  onSubmit,
  onBackBtnClickHandler,
  isSubmitAPILoading,
}: SalaryUIProps) => {
  const {
    handleSubmit,
    getValues,
    formState: { errors },
    control,
    setValue,
    setError,
    clearErrors,
  } = useForm();

  const siteConfig = getSiteProps(window.location.hostname);
  const defaultCurrencySelected = {
    value: siteConfig?.currency_name,
    id: siteConfig?.currency_id,
    text: siteConfig?.currency_code,
  };

  const salaryMode =
    siteConfig?.site_map === "rexmonster"
      ? SalaryModeOptions[1]
      : SalaryModeOptions[0];

  const updatedSalary = getSalaryBasedOnSiteContext({
    absoluteSalary: prefilledData?.salaryPrefill?.salary?.absoluteValue
      ? (prefilledData?.salaryPrefill?.salary?.absoluteValue as number)
      : 0,
    siteContext: siteConfig?.site_map ?? "rexmonster",
    salaryMode: (prefilledData?.salaryPrefill as any)?.salary?.salaryMode?.id
      ? (prefilledData?.salaryPrefill as any)?.salary?.salaryMode
      : salaryMode,
  });

  const prefillDataHelper = (prefillData: any) => {
    const { salaryPrefill } = prefillData;

    if (salaryPrefill) {
      const { salary } = salaryPrefill;
      if (salary?.absoluteValue) {
        setValue("salary", updatedSalary.toString());
      }
      if (salary?.currency) {
        setValue("currency", {
          value: salary.currency,
          id: salary.currency,
          text: salary.currency,
        });
      }
    }
  };

  useEffect(() => {
    if (prefilledData) {
      prefillDataHelper(prefilledData);
    }
  }, [prefilledData]);

  const onSubmitWrapper = (data: any) => {
    onSubmit(data, salaryMode, siteConfig?.site_map === "rexmonster");
  };

  const salaryValue = getValues("salary");

  return (
    <>
      <Heading
        headingText="Is your salary correct?"
        isMobile={window.innerWidth < 768}
        onBackBtnClickHandler={onBackBtnClickHandler}
      />

      <form
        className="l1-mb-20 l1-mt-12 md:l1-mb-20 md:l1-mt-14 l1-flex l1-flex-col l1-gap-3 l1-min-h-[200px] l1-h-[200px] md:!l1-w-[328px]"
        id="salaryForm"
        onSubmit={handleSubmit(onSubmitWrapper)}
        noValidate
      >
        <Label
          labelText={`Current ${salaryMode.name === "Annually" ? "Annual" : salaryMode.name} Salary`}
          isRequired={true}
          className="!l1-text-sm"
        >
          <SalaryInputController
            name="salary"
            control={control}
            isRequired={true}
            placeholder={
              siteConfig?.site_map === "rexmonster"
                ? "Enter Salary"
                : "Enter Salary (monthly)"
            }
            currency={getValues("currency") ?? defaultCurrencySelected}
            setCurrency={(value: unknown) => {
              setValue("currency", value);
            }}
            absoluteSalary={
              getValues("salary")
                ? getValues("salary")
                : prefilledData?.salaryPrefill?.salary?.absoluteValue &&
                    prefilledData?.salaryPrefill?.salary?.absoluteValue.toString()
                  ? updatedSalary.toString()
                  : ""
            }
            setAbsoluteSalary={(value: string) => {
              if (value === "0") {
                setError(
                  "salary",
                  {
                    type: "",
                    message: "Salary value cannot be 0",
                  },
                  { shouldFocus: true },
                );
              } else {
                clearErrors("salary");
              }
              setValue("salary", value);
            }}
            isSalaryAnnual={siteConfig?.site_map === "rexmonster"}
            errorMessage={
              errors.salary
                ? errors.salary?.message
                  ? errors.salary.message.toString()
                  : "This field is required"
                : ""
            }
            // tooltipText={
            //   siteConfig?.site_map === "rexmonster"
            //     ? "To rank high in recruiter searches, ensure you enter the correct annual salary."
            //     : undefined
            // }
          />
        </Label>

        {salaryValue && Number(salaryValue) ? (
          <HighLowSalaryWarning
            absoluteSalary={
              salaryValue && Number(salaryValue)
                ? Number(salaryValue)
                : updatedSalary && Number(updatedSalary)
                  ? Number(updatedSalary)
                  : 0
            }
          />
        ) : null}

        <ActionButtonContainer
          primaryBtnLabel="Save"
          isLoading={isSubmitAPILoading}
        />
      </form>
    </>
  );
};
